import {
  CSSProperties,
  FunctionComponent,
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { IconButton } from '../../controls/iconButton/IconButton';
import { Icon, Icons } from '../../icons';
import { L12nText } from '../../text/L12nText';

import styles from './modal.module.css';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  title?: ReactNode;
  tk?: string;
  onClose?: () => void;
  onFullScreenClose?: () => void;
  fullScreenNoClose?: boolean;
  onBack?: () => void;
  small?: boolean;
  large?: boolean;
  floating?: boolean;
  draggable?: boolean;
  grow?: boolean;
  footer?: ReactNode;
  isHistoryModal?: boolean;
  maxBodyHeight?: number;
  placement?: 'center' | 'start' | 'end';
}

interface Vector {
  x: number;
  y: number;
}

export const Modal: FunctionComponent<ModalProps> = ({
  open,
  onClose,
  onFullScreenClose,
  fullScreenNoClose,
  onBack,
  title,
  tk,
  children,
  small,
  large,
  floating,
  draggable,
  grow,
  footer,
  isHistoryModal,
  maxBodyHeight,
  placement = 'center',
  ...rest
}) => {
  const audioTrigger = useAudioTrigger();
  const [shouldRender, setShouldRender] = useState(open);
  const modalRef = useRef<HTMLDivElement>(null);
  const modalWindowRef = useRef<HTMLDivElement>(null);
  const mods = useModifiers(styles, 'modal', {
    small,
    large,
    floating,
    draggable,
    grow,
    open,
    center: placement === 'center',
    start: placement === 'start',
    end: placement === 'end',
  });
  const [offset, setOffset] = useState<Vector>({ x: 0, y: 0 });

  useEffect(() => {
    let timeoutId: number;
    if (open) {
      setShouldRender(true);
      setOffset({ x: 0, y: 0 });
    } else {
      modalRef?.current?.classList.add(styles.modal_closing);
      timeoutId = window.setTimeout(() => {
        modalRef?.current?.classList.remove(styles.modal_closing);
        setShouldRender(false);
      }, 300);
    }
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  const handlePointerDown = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (!draggable || !modalRef.current) return;

    let offsetVector: Vector = { ...offset };
    const clickOriginVector: Vector = {
      x: event.clientX,
      y: event.clientY,
    };

    const clampOffset = (offset: Vector): Vector => {
      if (offset.y < 0) {
        offset.y = 0;
      }
      return offset;
    };

    const updateOffsetVector = (event: MouseEvent): void => {
      offsetVector = clampOffset({
        x: event.clientX - clickOriginVector.x + offset.x,
        y: event.clientY - clickOriginVector.y + offset.y,
      });
      setOffset(offsetVector);
    };

    const handlePointerMove = (event: MouseEvent): void => {
      updateOffsetVector(event);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    const handlePointerUp = (): void => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleClose = (): void => {
    onClose?.();
  };

  const handleFullScreenClose = (): void => {
    onClose?.();
    onFullScreenClose?.();
  };

  return (
    <div className={`${styles.modal} ${mods}`} ref={modalRef} data-testid="modal">
      {shouldRender ? (
        <div
          data-testid="modal-window"
          className={`${styles.modal__window}`}
          style={{ '--offset-x': `${offset.x}px`, '--offset-y': `${offset.y}px` } as CSSProperties}
          ref={modalWindowRef}
          {...rest}
        >
          <div data-testid="modal-header" className={styles.modal__header} onPointerDown={handlePointerDown}>
            <div className={styles.modal__headerWing}>
              <div className={`${styles.modal__action}`}>
                <button
                  className={`${styles.modal__back} ${onBack ? styles.modal__back_visible : ''}`}
                  onClick={(): void => {
                    if (onBack) {
                      onBack();
                    } else {
                      handleClose();
                    }
                    audioTrigger.play({ name: 'menuClick' });
                  }}
                >
                  <Icon icon={Icons.ChevronLeft} />
                  <span className={styles.modal__backLabel}>
                    <L12nText tk="BACK" />
                  </span>
                </button>
              </div>
            </div>
            <div className={styles.modal__headerTitle}>{tk ? <L12nText tk={tk} /> : title}</div>
            <div className={styles.modal__headerWing}>
              <div
                className={`${styles.modal__action} ${
                  onFullScreenClose || fullScreenNoClose ? styles.modal__action_fullScreenHidden : ''
                }`}
              >
                {onClose ? <IconButton icon={Icons.Close} onClick={handleClose} ghost small /> : null}
              </div>
              {onFullScreenClose ? (
                <div className={`${styles.modal__action} ${styles.modal__action_fullScreenVisible}`}>
                  {onClose ? <IconButton icon={Icons.Close} onClick={handleFullScreenClose} ghost small /> : null}
                </div>
              ) : null}
            </div>
          </div>
          <div className={styles.modal__body} style={{ maxHeight: maxBodyHeight }}>
            {children}
          </div>
          {footer && (
            <div
              data-testid="modal-footer"
              className={`${styles.modal__footer} ${isHistoryModal ? styles.modal__footer_history : ''}`}
            >
              {footer}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
